import { ApiError } from "../api";
import type {
    DashboardOut,
    SuperEnrollOut,
    SuperLoginOk,
    SuperLoginResponse,
    WhoAmIOut,
} from "@saquei/types";
import { authedJson } from "./auth-fetch";

const TOKEN_KEY = "saquei.super.token";
const ENROLL_TOKEN_KEY = "saquei.super.enroll";
const CHALLENGE_TOKEN_KEY = "saquei.super.challenge";

export interface SuperAuthState {
    token: string;
    superAdminId: string;
}

export function loadSuperAuth(): SuperAuthState | null {
    if (typeof localStorage === "undefined") return null;
    try {
        const raw = localStorage.getItem(TOKEN_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as SuperAuthState;
    } catch {
        return null;
    }
}
export function saveSuperAuth(s: SuperAuthState): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(s));
}
export function clearSuperAuth(): void {
    localStorage.removeItem(TOKEN_KEY);
}

export function setSuperEnrollToken(t: string) { sessionStorage.setItem(ENROLL_TOKEN_KEY, t); }
export function getSuperEnrollToken(): string | null { return sessionStorage.getItem(ENROLL_TOKEN_KEY); }
export function clearSuperEnrollToken() { sessionStorage.removeItem(ENROLL_TOKEN_KEY); }
export function setSuperChallengeToken(t: string) { sessionStorage.setItem(CHALLENGE_TOKEN_KEY, t); }
export function getSuperChallengeToken(): string | null { return sessionStorage.getItem(CHALLENGE_TOKEN_KEY); }
export function clearSuperChallengeToken() { sessionStorage.removeItem(CHALLENGE_TOKEN_KEY); }

// Rotas publicas de login (sem token bearer) -- NAO usam authedJson.
async function postJson<T>(url: string, body: unknown): Promise<T> {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
            const b = await r.json();
            if (b?.error?.message) msg = String(b.error.message);
        } catch {}
        throw new ApiError(r.status, msg);
    }
    return (await r.json()) as T;
}

export function superLogin(email: string, password: string): Promise<SuperLoginResponse> {
    return postJson<SuperLoginResponse>("/api/v1/super/auth/login", { email, password });
}
export function superEnroll(enroll_token: string): Promise<SuperEnrollOut> {
    return postJson<SuperEnrollOut>("/api/v1/super/auth/totp/enroll", { enroll_token });
}
export function superConfirm(enroll_token: string, code: string): Promise<SuperLoginOk> {
    return postJson<SuperLoginOk>("/api/v1/super/auth/totp/confirm", { enroll_token, code });
}
export function superVerify(
    challenge_token: string,
    code: string,
    recovery: boolean,
): Promise<SuperLoginOk> {
    return postJson<SuperLoginOk>("/api/v1/super/auth/totp/verify", {
        challenge_token,
        code,
        recovery,
    });
}

/**
 * Le o token CSRF do cookie `saquei_super_csrf` setado pelo backend no
 * login super. O cookie e propositadamente NAO-HttpOnly pra JS conseguir
 * ler -- pattern double-submit. Atacante CSRF puro consegue mandar o
 * cookie automaticamente mas nao consegue ler pra echoar no header.
 *
 * Retorna null se o cookie nao existe (nao logado, expirou, ou logout
 * limpou). Caller deve tratar como sessao invalida.
 */
export function getSuperCsrfToken(): string | null {
    if (typeof document === "undefined") return null;
    for (const pair of document.cookie.split(";")) {
        const trimmed = pair.trim();
        if (trimmed.startsWith("saquei_super_csrf=")) {
            const v = trimmed.slice("saquei_super_csrf=".length);
            return v.length > 0 ? v : null;
        }
    }
    return null;
}

/** Headers prontos pra um POST super-critico (suspend/cancel/reactivate/
 * reset-password/reset-mfa). Inclui Authorization Bearer (compat) + o
 * header x-csrf-token requerido pelo middleware csrf_double_submit. */
export function superCriticalHeaders(token: string): Record<string, string> {
    const h: Record<string, string> = {
        authorization: `Bearer ${token}`,
        "content-type": "application/json",
    };
    const csrf = getSuperCsrfToken();
    if (csrf) h["x-csrf-token"] = csrf;
    return h;
}

// Rotas autenticadas usam authedJson com scope='super'.
export function superWhoami(token: string): Promise<WhoAmIOut> {
    return authedJson("super", token, "/api/v1/super/auth/whoami");
}
export function superDashboard(token: string): Promise<DashboardOut> {
    return authedJson("super", token, "/api/v1/super/dashboard");
}
